import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import BuyerInbox from '../system-modules/favorites/components/BuyerInbox';

function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Status display mapping
const statusConfig = {
  pending: { label: 'Pending', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  claimed: { label: 'Claimed', color: 'text-blue-600', bg: 'bg-blue-50' },
  in_transit: { label: 'In Transit', color: 'text-purple-600', bg: 'bg-purple-50' },
  delivered: { label: 'Delivered', color: 'text-green-600', bg: 'bg-green-50' },
  cancelled: { label: 'Cancelled', color: 'text-red-600', bg: 'bg-red-50' },
  refunded: { label: 'Refunded', color: 'text-gray-600', bg: 'bg-gray-50' },
  damaged: { label: 'Damaged', color: 'text-orange-600', bg: 'bg-orange-50' }
};

export default function PickupDashboard() {
  const { user } = useAuth();
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState({ avail: true, my: true, credits: true });
  const [activeTab, setActiveTab] = useState('available');
  const [sortBy, setSortBy] = useState('pickupDist');
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Filters
  const [dateRange, setDateRange] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [buyerSearch, setBuyerSearch] = useState('');
  const [txDateRange, setTxDateRange] = useState('all');

  // Initialize balance from user data on mount
  useEffect(() => {
    if (user?.pickupSettings?.creditsBalance !== undefined) {
      setBalance(user.pickupSettings.creditsBalance);
    }
  }, [user]);

  // Fetch unread count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch('/api/favorites/communication/unread/count', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    fetchUnreadCount();
  }, []);

  // Pre-fetch credits and deliveries data when component mounts
  useEffect(() => {
    fetchCredits();
    fetchMyDeliveries();
  }, []);

  useEffect(() => {
    if (activeTab === 'available') fetchAvailableOrders();
    else if (activeTab === 'my') fetchMyDeliveries();
    else if (activeTab === 'credits') fetchCredits();
  }, [activeTab, dateRange, statusFilter, buyerSearch, txDateRange, showAllOrders]);

  const fetchAvailableOrders = async () => {
    setLoading(prev => ({ ...prev, avail: true }));
    try {
      const url = showAllOrders ? '/api/orders/available-pickups?debug=true' : '/api/orders/available-pickups';
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setAvailableOrders(Array.isArray(data) ? data : []);
    } catch (error) { console.error(error); } finally { setLoading(prev => ({ ...prev, avail: false })); }
  };

  const fetchMyDeliveries = async () => {
    setLoading(prev => ({ ...prev, my: true }));
    try {
      const res = await fetch('/api/orders/my-deliveries', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      let data = await res.json();
      if (!Array.isArray(data)) data = [];
      // Apply filters
      if (dateRange !== 'all') {
        const days = parseInt(dateRange);
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - days);
        data = data.filter(o => new Date(o.createdAt) >= cutoff);
      }
      if (statusFilter !== 'all') data = data.filter(o => o.status === statusFilter);
      if (buyerSearch.trim()) {
        const term = buyerSearch.toLowerCase();
        data = data.filter(o => (o.buyer?.name && o.buyer.name.toLowerCase().includes(term)) ||
                                (o.buyer?.email && o.buyer.email.toLowerCase().includes(term)));
      }
      setMyDeliveries(data);
    } catch (error) { console.error(error); } finally { setLoading(prev => ({ ...prev, my: false })); }
  };

  const fetchCredits = async () => {
    setLoading(prev => ({ ...prev, credits: true }));
    try {
      const [balanceRes, txRes] = await Promise.all([
        fetch('/api/credits/balances', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/credits/transactions', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      const balances = await balanceRes.json();
      const totalBalance = balances.reduce((sum, b) => sum + b.balance, 0);
      setBalance(totalBalance);

      let transactionsArr = await txRes.json();
      if (transactionsArr.transactions) transactionsArr = transactionsArr.transactions;
      if (!Array.isArray(transactionsArr)) transactionsArr = [];
      
      if (txDateRange !== 'all') {
        const days = parseInt(txDateRange);
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        transactionsArr = transactionsArr.filter(t => new Date(t.createdAt) >= cutoff);
      }
      setTransactions(transactionsArr);
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(prev => ({ ...prev, credits: false }));
    }
  };

  const claimOrder = async (orderId) => {
    try {
      const res = await fetch(`/api/orders/${orderId}/claim`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        alert('Order claimed!');
        fetchAvailableOrders();
        fetchMyDeliveries();
      } else {
        const error = await res.json();
        alert(error.error || 'Claim failed');
      }
    } catch (error) { alert('Claim failed'); }
  };

  const updateDeliveryStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        alert(`Status updated to ${newStatus.replace('_', ' ')}`);
        fetchMyDeliveries();
      } else {
        const error = await res.json();
        alert(error.message || 'Update failed');
      }
    } catch (error) {
      console.error(error);
      alert('Update failed');
    }
  };

  const getSellerName = (order) => {
    if (order.hubId) return order.hubId.name || 'Hub';
    if (order.producer) return order.producer.farmName || order.producer.name || 'Producer';
    return 'Unknown';
  };

  const getSellerAddress = (order) => {
    if (order.hubId && order.hubId.location) {
      const loc = order.hubId.location;
      return `${loc.address}, ${loc.suburb} ${loc.state} ${loc.postcode}`;
    }
    if (order.producer && order.producer.location) {
      const loc = order.producer.location;
      return `${loc.address}, ${loc.suburb} ${loc.state} ${loc.postcode}`;
    }
    return 'Address not available';
  };

  const getDistanceToPickupPoint = (order) => {
    if (!order.pickupLocation || !user?.location?.coordinates) return null;
    const [lon1, lat1] = user.location.coordinates;
    const [lon2, lat2] = order.pickupLocation;
    return getDistance(lat1, lon1, lat2, lon2);
  };

  const getDistanceToDelivery = (order) => {
    if (!order.deliveryLocation?.coordinates || !user?.location?.coordinates) return null;
    const [lon1, lat1] = user.location.coordinates;
    const [lon2, lat2] = order.deliveryLocation.coordinates;
    return getDistance(lat1, lon1, lat2, lon2);
  };

  const sortedOrders = [...availableOrders].sort((a, b) => {
    if (sortBy === 'pickupDist') {
      const da = getDistanceToPickupPoint(a) ?? Infinity;
      const db = getDistanceToPickupPoint(b) ?? Infinity;
      return da - db;
    }
    if (sortBy === 'deliveryDist') {
      const da = getDistanceToDelivery(a) ?? Infinity;
      const db = getDistanceToDelivery(b) ?? Infinity;
      return da - db;
    }
    if (sortBy === 'amount') return b.totalAmount - a.totalAmount;
    return 0;
  });

  const formatTransactionDescription = (tx) => {
    const rewardMatch = tx.description.match(/Reward for delivering order (\w+) to (.*?) \(\$([\d.]+), (\d+) km\)/);
    if (rewardMatch) {
      const [, orderId, customerName, amount, distance] = rewardMatch;
      return `🎁 Order #${orderId.slice(-6)}: Delivered to ${customerName} (${distance}km) – Earned ${tx.amount} credits`;
    }
    if (tx.amount < 0) {
      return `🛒 Redeemed ${Math.abs(tx.amount)} credits: ${tx.description}`;
    }
    return tx.description;
  };

  // Tab configuration
  const tabs = [
    { id: 'available', label: `Available Orders (${availableOrders.length})` },
    { id: 'my', label: `My Deliveries (${myDeliveries.length})` },
    { id: 'credits', label: `Credits (${balance})` },
    { id: 'inbox', label: `📬 Inbox${unreadCount > 0 ? ` (${unreadCount})` : ''}` }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold mb-2">Pickup Dashboard</h1>
        <button onClick={() => activeTab === 'available' ? fetchAvailableOrders() : (activeTab === 'my' ? fetchMyDeliveries() : (activeTab === 'credits' ? fetchCredits() : null))} className="bg-blue-500 text-white px-3 py-1 rounded">Refresh</button>
      </div>
      <p className="text-gray-600 mb-6">Welcome, {user?.name}!</p>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-sm text-gray-500">Completed Deliveries</div>
          <div className="text-2xl font-bold">{user?.pickupSettings?.completedDeliveries || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-sm text-gray-500">Rating</div>
          <div className="text-2xl font-bold">{user?.pickupSettings?.rating || 5.0} ★</div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex flex-wrap gap-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 px-1 ${activeTab === tab.id ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'available' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2 items-center flex-wrap">
              <label className="text-sm text-gray-600 self-center">Sort by:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border rounded px-2 py-1 text-sm">
                <option value="pickupDist">Distance to pickup (closest)</option>
                <option value="deliveryDist">Distance to delivery (closest)</option>
                <option value="amount">Order amount (highest)</option>
              </select>
              
              <label className="flex items-center gap-2 ml-4">
                <input
                  type="checkbox"
                  checked={showAllOrders}
                  onChange={(e) => setShowAllOrders(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-600">Show all orders (debug)</span>
              </label>
            </div>
          </div>
          {loading.avail ? <div className="text-center py-8">Loading...</div> : availableOrders.length === 0 ? <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No orders available.</div> : (
            <div className="space-y-4">
              {sortedOrders.map(order => {
                const pickupDist = getDistanceToPickupPoint(order);
                const deliveryDist = getDistanceToDelivery(order);
                const sellerAddress = getSellerAddress(order);
                return (
                  <div key={order._id} className="bg-white rounded-lg shadow-md p-4 flex justify-between items-center">
                    <div className="flex-1">
                      <p className="font-semibold">Order #{order._id.slice(-6).toUpperCase()}</p>
                      <p className="text-sm text-gray-500">Seller: {getSellerName(order)}</p>
                      <p className="text-sm text-gray-500">Buyer: {order.buyer?.name}</p>
                      <p className="text-sm text-gray-500">Pickup from: {sellerAddress}</p>
                      <p className="text-sm text-gray-500">
                        Delivery to: {order.deliveryAddress?.address}, {order.deliveryAddress?.suburb} {order.deliveryAddress?.state} {order.deliveryAddress?.postcode}
                      </p>
                      <p className="text-sm text-gray-500">Total: ${order.totalAmount.toFixed(2)}</p>
                      {order.drivingDistance && (
                        <p className="text-sm text-blue-600">📏 Driving distance: {order.drivingDistance.toFixed(1)} km</p>
                      )}
                      {pickupDist !== null && <p className="text-sm text-blue-600">🚚 Pickup distance: {pickupDist.toFixed(1)} km</p>}
                      {deliveryDist !== null && <p className="text-sm text-blue-600">🏠 Delivery distance: {deliveryDist.toFixed(1)} km</p>}
                    </div>
                    <button onClick={() => claimOrder(order._id)} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 ml-4">Claim</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'my' && (
        <div>
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div><label className="block text-sm font-medium text-gray-700">Date Range</label><select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="mt-1 block w-full border rounded-md px-3 py-2"><option value="all">All time</option><option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option></select></div>
              <div><label className="block text-sm font-medium text-gray-700">Order Status</label><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="mt-1 block w-full border rounded-md px-3 py-2">
                <option value="all">All</option>
                <option value="claimed">Claimed</option>
                <option value="in_transit">In Transit</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select></div>
              <div className="flex-1"><label className="block text-sm font-medium text-gray-700">Buyer Name/Email</label><input type="text" value={buyerSearch} onChange={(e) => setBuyerSearch(e.target.value)} placeholder="Search..." className="mt-1 block w-full border rounded-md px-3 py-2" /></div>
              <button onClick={() => { setDateRange('all'); setStatusFilter('all'); setBuyerSearch(''); }} className="bg-gray-200 px-4 py-2 rounded text-sm">Clear</button>
            </div>
          </div>
          {loading.my ? <div className="text-center py-8">Loading...</div> : myDeliveries.length === 0 ? <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No assigned deliveries.</div> : (
            <div className="space-y-4">
              {myDeliveries.map(order => {
                const sellerAddress = getSellerAddress(order);
                const statusInfo = statusConfig[order.status] || { label: order.status, color: 'text-gray-600', bg: 'bg-gray-50' };
                return (
                  <div key={order._id} className="bg-white rounded-lg shadow-md p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold">Order #{order._id.slice(-6).toUpperCase()}</p>
                        <p className="text-sm text-gray-500">Seller: {getSellerName(order)}</p>
                        <p className="text-sm text-gray-500">Buyer: {order.buyer?.name}</p>
                        <p className="text-sm text-gray-500">Pickup from: {sellerAddress}</p>
                        <p className="text-sm text-gray-500">
                          Delivery to: {order.deliveryAddress?.address}, {order.deliveryAddress?.suburb} {order.deliveryAddress?.state} {order.deliveryAddress?.postcode}
                        </p>
                        <p className="text-sm text-gray-500">Total: ${order.totalAmount.toFixed(2)}</p>
                        <p className={`text-sm ${statusInfo.color} ${statusInfo.bg} inline-block px-2 py-1 rounded mt-1`}>
                          Status: {statusInfo.label}
                        </p>
                      </div>
                      <select
                        value={order.status}
                        onChange={(e) => updateDeliveryStatus(order._id, e.target.value)}
                        className="border rounded px-2 py-1 text-sm ml-4"
                        disabled={order.status === 'delivered' || order.status === 'cancelled'}
                      >
                        <option value="claimed">Claimed</option>
                        <option value="in_transit">In Transit</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                    
                    {/* Earnings breakdown - shown for delivered orders */}
                    {order.status === 'delivered' && order.deliveryReward && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="font-semibold text-green-600">💰 Earnings</p>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                          <div>
                            <span className="text-gray-500">Distance:</span>
                            <span className="ml-1 font-medium">{order.deliveryDistance?.toFixed(1)} km</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Base:</span>
                            <span className="ml-1 font-medium">$5.00</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Bonus:</span>
                            <span className="ml-1 font-medium">${order.deliveryRewardBreakdown?.distanceBonus?.toFixed(2) || '0.00'}</span>
                          </div>
                        </div>
                        <div className="mt-1 pt-1 border-t">
                          <span className="text-gray-500">Total reward:</span>
                          <span className="ml-2 font-bold text-green-600">${order.deliveryReward?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'credits' && (
        <div>
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div><label className="block text-sm font-medium text-gray-700">Transaction Date Range</label><select value={txDateRange} onChange={(e) => setTxDateRange(e.target.value)} className="mt-1 block w-full border rounded-md px-3 py-2"><option value="all">All time</option><option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option></select></div>
          </div>
          {loading.credits ? <div className="text-center py-8">Loading...</div> : (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-center mb-6"><div className="text-4xl font-bold text-green-600">{balance} credits</div><p className="text-gray-500">Available to spend on your next order</p></div>
              <h3 className="font-semibold text-lg mb-3">Transaction History</h3>
              {transactions.length === 0 ? <p className="text-gray-500">No transactions yet.</p> : (
                <div className="space-y-2">
                  {transactions.map(tx => (
                    <div key={tx._id} className="flex justify-between border-b py-2">
                      <div>
                        <p className="text-sm">{formatTransactionDescription(tx)}</p>
                        <p className="text-xs text-gray-400">{new Date(tx.createdAt).toLocaleString()}</p>
                      </div>
                      <div className={`font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'inbox' && (
        <div>
          <BuyerInbox />
        </div>
      )}
    </div>
  );
}
